import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import HeaderStats from '../components/HeaderStats';
import FilterSection from '../components/FilterSection';
import ConfidenceTiers from '../components/ConfidenceTiers';
import { fetchPredictions, enrichPredictionsWithResults } from '../utils/api';
import { useSport } from '../context/SportContext';
import MLBDashboard from './MLBDashboard';
import NFLDashboard from './NFLDashboard';
import CFBDashboard from './CFBDashboard';
import NHLDashboard from './NHLDashboard';
import CBBDashboard from './CBBDashboard';
import NBADashboard from './NBADashboard';
const Dashboard = () => {
    const { currentSport } = useSport();
    const [predictions, setPredictions] = useState(null);
    const [filteredGames, setFilteredGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        team: '',
        confidence: 'ALL',
        conference: 'ALL',
        week: 'ALL',
        date: 'ALL'
    });
    useEffect(() => {
        const loadPredictions = async () => {
            try {
                setLoading(true);
                console.log('🔄 Loading predictions for sport:', currentSport.id);
                const data = await fetchPredictions(currentSport.id);
                console.log('✅ Predictions loaded:', data.games?.length, 'games');
                // Enrich predictions with game results and bet outcomes
                const enrichedData = await enrichPredictionsWithResults(data, currentSport.id);
                console.log('✅ Predictions enriched with results');
                setPredictions(enrichedData);
                setFilteredGames(enrichedData.games);
            }
            catch (err) {
                setError('Failed to load predictions. Please try again later.');
                console.error('❌ Error loading predictions:', err);
            }
            finally {
                setLoading(false);
            }
        };
        loadPredictions();
    }, [currentSport]);
    useEffect(() => {
        if (!predictions)
            return;
        let filtered = predictions.games;
        // Apply team filter
        if (filters.team) {
            const teamLower = filters.team.toLowerCase();
            filtered = filtered.filter(game => game.home_team.toLowerCase().includes(teamLower) ||
                game.away_team.toLowerCase().includes(teamLower));
        }
        // Apply confidence filter
        if (filters.confidence !== 'ALL') {
            filtered = filtered.filter(game => game.confidence_level === filters.confidence);
        }
        // Apply conference filter
        if (filters.conference !== 'ALL') {
            filtered = filtered.filter(game => {
                // Check if either team is from the selected conference
                const gameData = game.raw_data;
                const homeConference = gameData?.team_analysis?.home_team_stats?.conference;
                const awayConference = gameData?.team_analysis?.away_team_stats?.conference;
                return game.conference === filters.conference ||
                    homeConference === filters.conference ||
                    awayConference === filters.conference;
            });
        }
        // Apply week filter
        if (filters.week !== 'ALL') {
            filtered = filtered.filter(game => game.week === parseInt(filters.week));
        }
        setFilteredGames(filtered);
    }, [predictions, filters]);
    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
    };
    if (loading) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "d-flex justify-content-center", children: _jsx("div", { className: "spinner-border", role: "status", children: _jsx("span", { className: "visually-hidden", children: "Loading..." }) }) }) }));
    }
    if (error) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsxs("div", { className: "alert alert-danger", role: "alert", children: [_jsx("i", { className: "fas fa-exclamation-triangle me-2" }), error] }) }));
    }
    if (!predictions) {
        return (_jsx("div", { className: "container-mobile safe-area-padding", children: _jsx("div", { className: "alert alert-info", role: "alert", children: "No predictions available at this time." }) }));
    }
    // If current sport is MLB, render MLB dashboard
    if (currentSport.id === 'mlb') {
        return _jsx(MLBDashboard, {});
    }
    // If current sport is NFL, render NFL dashboard  
    if (currentSport.id === 'nfl') {
        console.log('🏈 Routing to NFLDashboard for sport:', currentSport.id);
        return _jsx(NFLDashboard, {});
    }
    // If current sport is CFB, render CFB dashboard
    if (currentSport.id === 'cfb') {
        console.log('🏈 Routing to CFBDashboard for sport:', currentSport.id);
        return _jsx(CFBDashboard, {});
    }
    // If current sport is NHL, render NHL dashboard
    if (currentSport.id === 'nhl') {
        console.log('🏒 Routing to NHLDashboard for sport:', currentSport.id);
        return _jsx(NHLDashboard, {});
    }
    // If current sport is CBB, render CBB dashboard
    if (currentSport.id === 'cbb') {
        console.log('🏀 Routing to CBBDashboard for sport:', currentSport.id);
        return _jsx(CBBDashboard, {});
    }
    // If current sport is NBA, render NBA dashboard
    if (currentSport.id === 'nba') {
        console.log('🏀 Routing to NBADashboard for sport:', currentSport.id);
        return _jsx(NBADashboard, {});
    }
    console.log('📊 Using generic dashboard for sport:', currentSport.id);
    return (_jsxs("div", { className: "container-mobile safe-area-padding", children: [_jsx(HeaderStats, { metadata: predictions?.metadata, games: predictions?.games || [], sport: currentSport }), _jsx(FilterSection, { filters: filters, onFilterChange: handleFilterChange, availableWeeks: predictions?.games ? [...new Set(predictions.games.map(g => g.week))].sort() : [], availableConferences: predictions?.games ?
                    [...new Set(predictions.games
                            .flatMap(g => {
                            const gameData = g.raw_data;
                            const homeConf = gameData?.team_analysis?.home_team_stats?.conference;
                            const awayConf = gameData?.team_analysis?.away_team_stats?.conference;
                            return [g.conference, homeConf, awayConf].filter(conf => conf && conf !== null);
                        }))].sort() : [] }), _jsx(ConfidenceTiers, { games: filteredGames })] }));
};
export default Dashboard;
